/**
 * How a plate's rows are sized: each row exactly as tall as lets its pictures,
 * at their own proportions, fill the width side by side — the way a photo
 * album sets a row. Nothing is cropped and no white band is left, and upright
 * and landscape pictures mix of themselves.
 *
 * Shared by the PDF and the editor's preview, so what is arranged on screen
 * is what prints. All lengths are in whatever unit `width` is given in.
 */

import { captionLineCount } from './captionLines';

/**
 * The least a plate is shrunk to. Only captions of hundreds of words get it
 * there; the plate then outgrows maxHeight rather than losing its pictures.
 */
const MIN_SCALE = 0.25;

/** A picture of unknown shape counts as 4:3, the shape a camera takes. */
export const DEFAULT_ASPECT = 4 / 3;

/** The caption under each picture on the sheet: its size and its line. */
export const SHEET_CAPTION_FONT_SIZE = 7.5;
export const SHEET_CAPTION_LINE_HEIGHT = 1.2;

/**
 * A plate as the PDF sheets set it, in points. Here rather than beside the
 * sheet styles so the editor's preview can draw the same proportions without
 * loading the PDF library; `gridGeometry.test.ts` holds the width to the
 * sheet's figure width.
 */
export const SHEET_PLATE: PlateGeometryOptions = {
    /** The figure width of an A4 sheet: PHOTO_WIDTH. */
    width: 508.28,
    gap: 6,
    /** The caption's marginTop on the sheet. */
    captionGap: 2,
    captionLineHeight: SHEET_CAPTION_FONT_SIZE * SHEET_CAPTION_LINE_HEIGHT,
    captionLines: (text, width) =>
        captionLineCount(text, width, SHEET_CAPTION_FONT_SIZE),
    /** A single figure's height cap: SKETCH_MAX_HEIGHT. */
    maxRowHeight: 340,
    /**
     * The whole plate, captions included. It is atomic like a single figure,
     * so it has to fit a page with its band and Legende.
     */
    maxHeight: 600
};

/** SHEET_PLATE at another width — the editor's preview at screen size. */
export function sheetPlateAt(width: number): PlateGeometryOptions {
    const factor = width / SHEET_PLATE.width;
    return {
        width,
        gap: SHEET_PLATE.gap * factor,
        captionGap: SHEET_PLATE.captionGap * factor,
        captionLineHeight: SHEET_PLATE.captionLineHeight * factor,
        // Counted at the sheet's size, so the preview shrinks when print does.
        captionLines: (text, lineWidth) =>
            SHEET_PLATE.captionLines(text, lineWidth / factor),
        maxRowHeight: SHEET_PLATE.maxRowHeight * factor,
        maxHeight: SHEET_PLATE.maxHeight * factor
    };
}

export interface PlateGeometryOptions {
    /** The width a row fills. */
    width: number;
    /** Space between pictures in a row, and between rows. */
    gap: number;
    /** Space between a picture and its caption. */
    captionGap: number;
    /** The height of one caption line. */
    captionLineHeight: number;
    /**
     * How many lines a caption wraps to in a box this wide. A row leaves room
     * under it for its longest caption, never cutting one short.
     */
    captionLines: (text: string, width: number) => number;
    /**
     * The tallest a row may be. A row of one upright picture would otherwise
     * be as tall as the page; it stops here and stands narrower, centred.
     */
    maxRowHeight: number;
    /** The tallest the whole plate may be, captions included. */
    maxHeight: number;
}

export interface PlateRowGeometry {
    height: number;
    /** The room under the row for its captions, the longest one's. */
    captionHeight: number;
    /** One width per picture, in row order. */
    widths: number[];
}

export interface PlateGeometry {
    rows: PlateRowGeometry[];
    /**
     * Below 1 when the plate was shrunk as a whole to stay within `maxHeight`;
     * its rows then no longer fill the width.
     */
    scale: number;
}

/**
 * Width ÷ height of each picture, row by row; null where unknown. `captions`
 * holds each picture's caption text in the same places.
 */
export function plateGeometry(
    aspects: readonly (readonly (number | null)[])[],
    options: PlateGeometryOptions,
    captions: readonly (readonly string[])[] = []
): PlateGeometry {
    const rows = aspects.map((row) => {
        const known = row.map((aspect) =>
            aspect !== null && aspect > 0 ? aspect : DEFAULT_ASPECT
        );
        const room = options.width - options.gap * (known.length - 1);
        const total = known.reduce((sum, aspect) => sum + aspect, 0);
        const height = Math.min(room / total, options.maxRowHeight);
        return { height, widths: known.map((aspect) => aspect * height) };
    });
    const captionHeightsAt = (scale: number) =>
        rows.map((row, rowIndex) => {
            const lines = Math.max(
                1,
                ...row.widths.map((width, cellIndex) =>
                    options.captionLines(
                        captions[rowIndex]?.[cellIndex] ?? '',
                        width * scale
                    )
                )
            );
            return options.captionGap + lines * options.captionLineHeight;
        });
    const gaps = Math.max(0, rows.length - 1) * options.gap;
    const pictures = rows.reduce((sum, row) => sum + row.height, 0);
    const heightAt = (scale: number, captionHeights: number[]) =>
        pictures * scale +
        captionHeights.reduce((sum, height) => sum + height, 0) +
        gaps;
    // Shrinking narrows the captions, which may then wrap to more lines and
    // ask for a little more shrinking: a few rounds settle it. Where captions
    // are so long that shrinking only makes the plate TALLER, it stops there.
    let scale = 1;
    let captionHeights = captionHeightsAt(scale);
    for (let round = 0; round < 12; round++) {
        const height = heightAt(scale, captionHeights);
        if (pictures <= 0 || height <= options.maxHeight + 1e-9) {
            break;
        }
        const fixed = height - pictures * scale;
        const next = Math.max(
            MIN_SCALE,
            (options.maxHeight - fixed) / pictures
        );
        const nextCaptions = captionHeightsAt(next);
        if (next >= scale || heightAt(next, nextCaptions) >= height) {
            break;
        }
        scale = next;
        captionHeights = nextCaptions;
    }
    return {
        rows: rows.map((row, rowIndex) => ({
            height: row.height * scale,
            widths: row.widths.map((width) => width * scale),
            captionHeight: captionHeights[rowIndex]
        })),
        scale
    };
}
